// Frontend-only mock of the new billing/VAT endpoints.
// Mirrors the logic in ALPHA/app/Services/Billing/VatCalculator.php so the UI
// can be tested end-to-end before the backend is deployed.
//
// Activate via `.env.development.local`:
//   VUE_APP_MOCK_BILLING=true
//
// In production this module is unused (the env var must be unset).

const SELLER_COUNTRY_CODE = "RO";
const SELLER_COUNTRY_NAME = "Romania";
const STANDARD_RATE = 21;
const CONTACT_EMAIL = "imeicheck20@gmail.com";

let _logged = false;
export function isMockEnabled() {
    const enabled = String(process.env.VUE_APP_MOCK_BILLING).toLowerCase() === "true";
    if (enabled && !_logged) {
        _logged = true;
        // eslint-disable-next-line no-console
        console.info(
            "%c[billing-mock]%c VUE_APP_MOCK_BILLING=true — billing/VAT endpoints are stubbed in the frontend. " +
            "Delete .env.development.local to call the real backend.",
            "background:#7367f0;color:#fff;padding:2px 6px;border-radius:3px;font-weight:bold",
            "color:#7367f0"
        );
    }
    return enabled;
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * Pretend-VIES — accepts any VAT ID matching country prefix + 4-12 digits.
 * Returns a fake multi-line address mirroring the real VIES REST format so the
 * "Use VIES address" auto-fill flow can be tested.
 */
const MOCK_ADDRESSES_BY_COUNTRY = {
    DE: "Karl-Liebknecht-Str. 5\n10115 BERLIN\nGERMANY",
    FR: "23 RUE DU FAUBOURG SAINT-HONORE\n75008 PARIS\nFRANCE",
    IT: "Via Manzoni 3\n20121 MILANO\nITALY",
    ES: "Gran Via 31\n28013 MADRID\nSPAIN",
    NL: "Damrak 70\n1012AB AMSTERDAM\nNETHERLANDS",
    BE: "Rue Royale 100\n1000 BRUXELLES\nBELGIUM",
    AT: "Ringstrasse 1\n1010 WIEN\nAUSTRIA",
    PL: "Marszalkowska 1\n00-001 WARSZAWA\nPOLAND",
    BG: "Vitosha Boulevard 15\n1000 SOFIA\nBULGARIA",
    EL: "Stadiou 10\n10557 ATHENS\nGREECE",
    RO: "Strada Victoriei 1\n010101 BUCURESTI\nROMANIA",
};

export async function mockValidateVAT({ country, vat_id }) {
    await sleep(450);
    const cc = String(country || "").toUpperCase();
    const id = String(vat_id || "").replace(/\s+/g, "").toUpperCase();
    const stripped = id.startsWith(cc) ? id.slice(2) : id;
    const ok = /^[0-9A-Z]{4,12}$/.test(stripped);
    if (!ok) {
        return {
            valid: false,
            name: null,
            address: null,
            error: "VAT ID format is invalid (mock)",
        };
    }
    return {
        valid: true,
        name: `MOCK ${cc}-${stripped.slice(-4)} GMBH`,
        address: MOCK_ADDRESSES_BY_COUNTRY[cc] || `1 Mock Street\n00000 MOCK CITY\n${cc}`,
        error: null,
    };
}

/**
 * Mirrors the server-side VatCalculator logic.
 */
export function calculateVat(amount, billing) {
    const accountType = billing.account_type || "personal";
    const countryName = billing.country || null;
    const countryCode = billing.country_code || null;
    const isEU = !!billing.is_eu;
    const viesValid = !!billing.vies_valid;
    const subtotal = Number(amount) || 0;

    const compose = (rate, reverseCharge = false, note = null, blockedReason = null) => {
        const tax = Math.round((subtotal * rate) / 100 * 100) / 100;
        return {
            subtotal: Math.round(subtotal * 100) / 100,
            tax_rate: rate,
            tax_amount: tax,
            total: Math.round((subtotal + tax) * 100) / 100,
            reverse_charge: reverseCharge,
            vat_note: note,
            blocked_reason: blockedReason,
            currency: "USD",
        };
    };

    // EU B2B reverse charge
    if (
        accountType === "business" &&
        isEU &&
        countryCode !== SELLER_COUNTRY_CODE &&
        viesValid
    ) {
        return compose(
            0,
            true,
            "Reverse charge — Article 196 of Council Directive 2006/112/EC. VAT to be accounted for by the recipient."
        );
    }

    // Same-country (RO consumer)
    if (
        countryCode === SELLER_COUNTRY_CODE ||
        (countryName && countryName.toLowerCase() === SELLER_COUNTRY_NAME.toLowerCase())
    ) {
        return compose(STANDARD_RATE);
    }

    // EU consumer / EU biz w/o valid VAT
    if (isEU) return compose(STANDARD_RATE);

    // Non-EU export
    return compose(0, false, "Export of services — outside EU VAT scope.");
}

export async function mockPreview(payload) {
    await sleep(200);
    return calculateVat(payload.amount, payload.billing || {});
}

export async function mockUpdateBilling(billing) {
    await sleep(300);
    try {
        localStorage.setItem("mock_billing_profile", JSON.stringify(billing));
    } catch (_e) {
        /* ignore quota errors */
    }
    return billing;
}

export function loadMockBilling() {
    try {
        const raw = localStorage.getItem("mock_billing_profile");
        return raw ? JSON.parse(raw) : null;
    } catch (_e) {
        return null;
    }
}

export async function mockDownloadPdf(invoiceId) {
    await sleep(500);
    const text = [
        "MOCK INVOICE",
        "============",
        `Invoice ID: ${invoiceId}`,
        "",
        "This is a placeholder PDF generated by the frontend mock. The real",
        "PDF will be produced by the Laravel backend (barryvdh/laravel-dompdf)",
        "once the invoice issuance flow is wired up (Step 5).",
        "",
        `Generated: ${new Date().toISOString()}`,
    ].join("\n");
    return new Blob([text], { type: "text/plain" });
}
